import { FC, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useTheme } from '@mui/material';
import { observer } from 'mobx-react';

import CoreVirtualizedTable from '../ui/core/CoreVirtualizedTable';
import { Claim, CLAIMS_COLUMN_FILTER_TYPE, CLAIMS_TABLE_COLUMN_OPTIONS, ClaimsTableColumnId } from '../../types/claims';
import { useClaimsStore } from '../../hooks/useClaimsStore';
import ClaimsQueueSummary from './ClaimsQueueSummary';
import { getClaimsTableColumns } from './claimsTableColumns';
import { CLAIMS_FILTERABLE_COLUMNS } from './claimsColumnFilters';
import {
    ColumnFiltersConfig,
    ColumnFilterState,
    FilterOperator,
    SimpleColumnDef,
    SortingState,
} from '../../types/table';
import { useChatStore } from '../../hooks/useChatStore';
import { useMainStore } from '../../hooks/useMainStore';
import { loadPersistedClaimFilter } from '../helpers/claimFilterStorage';

interface ClaimsTableProps {
    selectTableRowCallback: (claimId: string | null) => void;
}

const ClaimsTable: FC<ClaimsTableProps> = observer(({ selectTableRowCallback }) => {
    const theme = useTheme()

    const {
        claimsData,
        isClaimsDataLoading,
        loadClaimsData,
        loadMoreClaims,
        activeTab,
        selectedQuickFilter,
        hasMoreClaims,
        isLoadingMore,
        orderBy,
        allFilters,
        assignedUsers,
        setOrderBy,
        setClaimToPreview,
        claimToPreview,
        claimKeywordSearch,
        setColumnFilter,
        clearColumnFilter,
        getUniqueColumnValues,
        getColumnFilterValues,
        getColumnFilterOperator,
        columnSettings,
        isColumnRequired,
        toggleRequiredColumn,
        visibleColumnIds,
    } = useClaimsStore();
    const chatStore = useChatStore();
    const { filtersScope } = useMainStore();

    // Convert store orderBy to SortingState for the table
    const sorting: SortingState = useMemo(() => {
        if (!orderBy) return [];
        return [{ id: orderBy.field, desc: orderBy.direction === 'desc' }];
    }, [orderBy]);
    const [tableHeight, setTableHeight] = useState<number>(500);
    const containerRef = useRef<HTMLDivElement>(null);

    // Calculate table height to fit the page
    const calculateHeight = useCallback(() => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const bottomPadding = 12; // Padding from bottom of page
            const calculatedHeight = viewportHeight - rect.top - bottomPadding;
            setTableHeight(Math.max(300, calculatedHeight)); // Minimum height of 300px
        }
    }, []);

    useEffect(() => {
        calculateHeight();
        window.addEventListener('resize', calculateHeight);
        return () => window.removeEventListener('resize', calculateHeight);
    }, [calculateHeight]);

    // calculateHeight measures our distance from the top of the viewport, so
    // anything laid out above us changes the answer whenever it changes height
    // -- the summary bar mounting, swapping its spinner for the taller metrics
    // card, or its caption wrapping when the claim preview narrows the column.
    // None of those fire `resize`, and the stale height leaves the table
    // overhanging the viewport. So watch the siblings above us directly.
    //
    // We observe them rather than ourselves: our own height IS the value being
    // set here, so observing it would feed back into itself (REV-2507).
    useEffect(() => {
        const container = containerRef.current;
        const parent = container?.parentElement;
        if (!parent || typeof ResizeObserver === 'undefined') return;

        const sizeObserver = new ResizeObserver(() => calculateHeight());

        const observeElementsAbove = () => {
            sizeObserver.disconnect();
            for (const child of Array.from(parent.children)) {
                if (child === container) break;
                sizeObserver.observe(child);
            }
            calculateHeight();
        };

        observeElementsAbove();

        // The summary bar renders nothing until it has totals, so the set of
        // siblings above us changes at runtime and has to be re-attached.
        const treeObserver = new MutationObserver(observeElementsAbove);
        treeObserver.observe(parent, { childList: true });

        return () => {
            sizeObserver.disconnect();
            treeObserver.disconnect();
        };
    }, [calculateHeight]);

    const { showMissingClaims } = useClaimsStore();

    useEffect(() => {
        loadClaimsData()
    }, [activeTab, selectedQuickFilter, orderBy, allFilters, showMissingClaims, claimKeywordSearch]);

    const filterableColumns = CLAIMS_FILTERABLE_COLUMNS;

    const columnFilterTypes = CLAIMS_COLUMN_FILTER_TYPE;

    // Build filters object from allFilters for column filter UI (operator + values)
    const columnFiltersFromAllFilters = useMemo(() => {
        const filters: Record<string, ColumnFilterState> = {};

        filterableColumns.forEach(columnId => {
            const values = getColumnFilterValues(columnId);
            const operator = getColumnFilterOperator(columnId);
            
            filters[columnId] = { operator, values };
        });
        return filters;
    }, [allFilters, assignedUsers, filterableColumns, getColumnFilterValues, getColumnFilterOperator]);

    // Column filters configuration
    const columnFiltersConfig: ColumnFiltersConfig = useMemo(() => ({
        filters: columnFiltersFromAllFilters,
        getOptions: getUniqueColumnValues,
        onApply: (columnId: string, values: string[], operator?: FilterOperator) =>
            setColumnFilter(columnId, values, operator),
        onClear: clearColumnFilter,
        filterableColumns,
        columnFilterTypes,
        isColumnPinned: isColumnRequired,
        onTogglePin: (columnId: string) => toggleRequiredColumn(columnId as ClaimsTableColumnId),
        canPinColumn: (columnId: string) => !CLAIMS_TABLE_COLUMN_OPTIONS.find(col => col.id === columnId)?.mandatory,
    }), [columnFiltersFromAllFilters, getUniqueColumnValues, setColumnFilter, clearColumnFilter, filterableColumns, columnFilterTypes, isColumnRequired, toggleRequiredColumn, columnSettings]);

    // Handle sorting change from the table
    const handleSortingChange = useCallback((newSorting: SortingState) => {
        if (newSorting.length === 0) {
            setOrderBy(null);
        } else {
            const { id, desc } = newSorting[0];
            setOrderBy({ field: id, direction: desc ? 'desc' : 'asc' });
        }
    }, [setOrderBy]);

    const columns = useMemo(() => getClaimsTableColumns(), []);

    // Visible columns in the store's display order (pinned first) -- the same
    // order the CSV export uses.
    const visibleColumns = useMemo(() => {
        const columnsById = new Map(columns.map(col => [col.id, col]));
        // The warnings icon column always stays first; it isn't part of the pin system
        // and relies on being first for its centered-icon styling.
        const warningsColumn = columns.filter(col => col.id === 'warnings');
        const rest = visibleColumnIds
            .map(id => columnsById.get(id))
            .filter((col): col is SimpleColumnDef<Claim> => !!col);
        return [...warningsColumn, ...rest];
    }, [columns, visibleColumnIds]);

    const handleRowClick = (claim: Claim) => {
        setClaimToPreview(claim);
        selectTableRowCallback(claim.claimNumber);

        chatStore.setClaimModeEnabled(true);
        chatStore.setIsClaimDataLoading(true);
        chatStore.loadClaimDetail(claim.claimNumber);
    };

    // Re-select whichever claim the sidebar's filter chip last remembered
    // (see Sidebar.tsx), once the table has loaded data to find it in. Skips
    // a persisted filter the user had cleared (chip X — isApplied: false),
    // since re-selecting it here would silently reapply a filter they turned
    // off. Runs once per mount and yields to an explicit selection (URL
    // claim_number, Sidebar's delete-chat redirect) already in flight.
    const hasAppliedPersistedClaimSelectionRef = useRef(false);

    useEffect(() => {
        if (hasAppliedPersistedClaimSelectionRef.current) {
            return;
        }
        if (claimToPreview) {
            hasAppliedPersistedClaimSelectionRef.current = true;
            return;
        }
        if (!claimsData || claimsData.length === 0) {
            return;
        }

        hasAppliedPersistedClaimSelectionRef.current = true;
        const persisted = loadPersistedClaimFilter(filtersScope);
        if (!persisted || !persisted.isApplied) {
            return;
        }

        const match = claimsData.find((claim) => claim.claimNumber === persisted.claimId);
        if (match) {
            handleRowClick(match);
        }
    }, [claimsData, claimToPreview]);

    return (
      <div
        ref={containerRef}
        style={{
          overflowX: "auto",
          background: "white",
          marginLeft: "12px",
          marginRight: "12px",
          marginBottom: "12px",
          borderRadius: "8px",
          border: "1px solid",
          borderColor: theme.palette.divider,
          // Column so the summary footer takes its height out of the rows'
          // share rather than pushing the card past the viewport. The card
          // owns the measured height; the rows get whatever the footer leaves.
          display: "flex",
          flexDirection: "column",
          height: `${tableHeight}px`,
        }}>
          <div style={{ flex: 1, minHeight: 0 }}>
            <CoreVirtualizedTable<Claim>
                isLoading={isClaimsDataLoading}
                data={claimsData || []}
                columns={visibleColumns}
                sorting={sorting}
                onSortingChange={handleSortingChange}
                height="100%"
                minWidth={500}
                onRowClick={handleRowClick}
                getRowId={(row) => row.id}
                getRowStyle={() => ({})}
                emptyMessage="No claims found"
                onLoadMore={loadMoreClaims}
                hasMore={hasMoreClaims}
                isLoadingMore={isLoadingMore}
                selectedRowId={claimToPreview?.id}
                columnFilters={columnFiltersConfig}
                disableLastOfTypeStyles
                isResizable
                pinColumns
            />
          </div>

          <ClaimsQueueSummary />
        </div>
    );
});

export default ClaimsTable;
